import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Info, AlertTriangle, CheckCircle2, Sparkles, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STYLE_CLS = {
  info:         'bg-brand_blue/15 text-brand_blue',
  warning:      'bg-amber-100/70 text-amber-800',
  success:      'bg-emerald-100/70 text-emerald-800',
  announcement: 'bg-purple-100/70 text-purple-800',
};
const STYLE_ICON = { info: Info, warning: AlertTriangle, success: CheckCircle2, announcement: Sparkles };
const SPEED_SEC = { slow: 60, normal: 40, fast: 25 };
const DISMISS_KEY = 'qimam:announcement-dismissed';
const CACHE_KEY   = 'qimam:announcement-cache';

// Read the last-known-good announcement from localStorage synchronously so the
// banner paints on the FIRST frame (no post-fetch layout shift on mobile Safari).
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// Global scrolling ticker at the top of every page. Content and settings are
// managed by super_admin via /super/announcement. Users may dismiss the banner
// per browser — a new updated_at value automatically re-shows it.
export default function AnnouncementBanner() {
  // Prime state from localStorage so first paint already includes the banner
  const [data, setData] = useState(() => readCache());
  const [dismissedKey, setDismissedKey] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; }
  });
  const bannerRef = useRef(null);

  const fetchOne = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/announcement`);
      setData(r.data || null);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(r.data || null)); } catch {}
    } catch { /* keep cached copy */ }
  }, []);

  useEffect(() => {
    fetchOne();
    const handler = () => fetchOne();
    window.addEventListener('announcement:updated', handler);
    const iv = setInterval(fetchOne, 60000);
    return () => {
      window.removeEventListener('announcement:updated', handler);
      clearInterval(iv);
    };
  }, [fetchOne]);

  // iOS Safari sometimes fails to re-paint the top of the viewport when an
  // element is inserted above the fold via React reconciliation. Nudge the
  // scroll position by 1px on next frame to force a repaint. Harmless on
  // desktop / Android (invisible to the user).
  useEffect(() => {
    if (!data || !data.active) return;
    const nudge = () => {
      if (window.scrollY === 0) {
        window.scrollTo(0, 1);
        requestAnimationFrame(() => window.scrollTo(0, 0));
      }
    };
    const t = setTimeout(nudge, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.active, data?.updated_at]);

  if (!data || !data.active) return null;
  const items = Array.isArray(data.items) ? data.items.filter((it) => it && it.text) : [];
  if (!items.length) return null;

  const currentKey = `${items.map((it) => it.text).join('|')}|${data.updated_at || ''}`;
  if (dismissedKey === currentKey) return null;

  const sec = SPEED_SEC[data.speed] ?? SPEED_SEC.normal;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, currentKey); } catch {}
    setDismissedKey(currentKey);
  };

  return (
    <div
      ref={bannerRef}
      className="announcement-marquee-viewport border-b border-border bg-white flex items-center gap-2 py-2 2xl:py-4 px-2 sm:px-4 2xl:px-6"
      data-testid="announcement-banner"
      dir="ltr"
    >
      <div className="announcement-marquee-track" style={{ animationDuration: `${sec}s` }}>
        {/* Two identical copies for a seamless loop */}
        {[0, 1].map((copy) => (
          <div key={copy} className="flex items-center gap-2 pl-2" aria-hidden={copy === 1}>
            {items.map((it, i) => {
              const Icon = STYLE_ICON[it.style] || Info;
              const cls = STYLE_CLS[it.style] || STYLE_CLS.info;
              return (
                <span
                  key={`${copy}-${i}`}
                  dir="rtl"
                  className={`inline-flex items-center gap-1.5 2xl:gap-3 px-2.5 py-1 2xl:px-4 2xl:py-2 rounded-md text-sm 2xl:text-2xl font-bold whitespace-nowrap ${cls}`}
                  style={{ unicodeBidi: 'isolate' }}
                  data-testid={copy === 0 ? `announcement-banner-item-${i}` : undefined}
                >
                  <Icon className="h-3.5 w-3.5 2xl:h-6 2xl:w-6 flex-shrink-0" />
                  <bdi>{it.text}</bdi>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="إخفاء الشريط"
        className="p-1 2xl:p-2 rounded-md hover:bg-black/5 flex-shrink-0"
        data-testid="announcement-banner-dismiss"
      >
        <X className="h-3.5 w-3.5 2xl:h-6 2xl:w-6" />
      </button>
    </div>
  );
}
